import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALANALYSISTYPES_LOAD,
    ACTION_TRIALANALYSISTYPES_LOAD_BY_ID,
    ACTION_TRIALANALYSISTYPES_LOAD_FAILURE,
    ACTION_TRIALANALYSISTYPES_LOAD_SUCCESS,

    ACTION_TRIALANALYSISTYPES_SAVE,
    ACTION_TRIALANALYSISTYPES_SAVE_SUCCESS,
    ACTION_TRIALANALYSISTYPES_SAVE_FAILURE,

    ACTION_TRIALANALYSISTYPES_CREATE,
    ACTION_TRIALANALYSISTYPES_CREATE_SUCCESS,
    ACTION_TRIALANALYSISTYPES_CREATE_FAILURE,

    ACTION_TRIALANALYSISTYPES_CLEAR,
    ACTION_TRIALANALYSISTYPES_FILTER,

    TrialAnalysisTypeAction
} from '../actions/trialAnalysisTypes';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialAnalysisTypeState {
    formData: Dtos.TrialAnalysisType | Dtos.TrialAnalysisType[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    formRoles: Dtos.RoleType[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialAnalysisType[];
}

const initialState: ITrialAnalysisTypeState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    formRoles: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: []
};


function loading(state: ITrialAnalysisTypeState, isLoading: boolean): ITrialAnalysisTypeState {
    const newState: ITrialAnalysisTypeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: ITrialAnalysisTypeState,
    formData: Dtos.TrialAnalysisType | Dtos.TrialAnalysisType[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialAnalysisTypeState {
    const newState: ITrialAnalysisTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: ITrialAnalysisTypeState, formFilter: IFormFilter): ITrialAnalysisTypeState {

    const trialAnalysisTypes: Dtos.TrialAnalysisType[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = trialAnalysisTypes ? trialAnalysisTypes.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: ITrialAnalysisTypeState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: ITrialAnalysisTypeState, responseStatus: Dtos.ResponseStatus): ITrialAnalysisTypeState {
    const newState: ITrialAnalysisTypeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: ITrialAnalysisTypeState, propertiesToClear?: string[]): ITrialAnalysisTypeState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: ITrialAnalysisTypeState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialAnalysisTypeState, isSaving: boolean): ITrialAnalysisTypeState {
    const newState: ITrialAnalysisTypeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialAnalysisTypeState,
    formData: Dtos.TrialAnalysisType | Dtos.TrialAnalysisType[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialAnalysisTypeState {
    const newState: ITrialAnalysisTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialAnalysisTypeState, responseStatus: Dtos.ResponseStatus): ITrialAnalysisTypeState {
    const newState: ITrialAnalysisTypeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialAnalysisTypeState, isCreating: boolean): ITrialAnalysisTypeState {
    const newState: ITrialAnalysisTypeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialAnalysisTypeState,
    formData: Dtos.TrialAnalysisType,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[]
): ITrialAnalysisTypeState {
    const newState: ITrialAnalysisTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialAnalysisTypeState, responseStatus: Dtos.ResponseStatus): ITrialAnalysisTypeState {
    const newState: ITrialAnalysisTypeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialAnalysisTypeReducer: Reducer<ITrialAnalysisTypeState> = (state: ITrialAnalysisTypeState = initialState, action: TrialAnalysisTypeAction) => {
    switch (action.type) {
        case ACTION_TRIALANALYSISTYPES_LOAD:
        case ACTION_TRIALANALYSISTYPES_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALANALYSISTYPES_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups, action.responseStatus);
       case ACTION_TRIALANALYSISTYPES_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALANALYSISTYPES_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALANALYSISTYPES_SAVE:
            return saving(state, true);
        case ACTION_TRIALANALYSISTYPES_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALANALYSISTYPES_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALANALYSISTYPES_CREATE:
            return creating(state, true);
        case ACTION_TRIALANALYSISTYPES_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups);
        case ACTION_TRIALANALYSISTYPES_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALANALYSISTYPES_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default TrialAnalysisTypeReducer;